import React, { FC, useEffect } from "react";
import {
  useTrackQuestionSetCompleted,
  useTrackQuestionSetViewed,
} from "kw/quoting/hooks/useTracking";

import { ChatFlow } from "kw/util/ChatFlow";
import { FlowLayout } from "kw/components/FlowLayout";
import { Placeholder } from "kanopi-components/Placeholder";
import { ProgressBarProps } from "kanopi-components/ProgressBar";
import { QuestionSet } from "kw/util/QuestionSet";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import { getQuestionSetAnswers } from "kw/util/getQuestionSetAnswers";
import { maybe } from "kanopi-components/utils/functional";
import { navigate } from "@reach/router";
import { useNavigate } from "@reach/router";
import { useStartQuestionFlow } from "kw/hooks/useStartQuestionFlow";
import { QuoteState } from "kw/api/zeus";

export enum QuestionFlowTypeEnum {
  Claim = "claim",
  Update = "update",
  Quote = "quote",
  PolicyHolder = "policyHolder",
  Exposure = "exposure",
  Cancel = "cancel",
}

export const QuestionFlowTypeKey = {
  claim: "claiming",
  update: "updating",
  quote: "quoting",
  policyHolder: "policyHolder",
};

type TQuestionFlowProps = {
  actionFlowType: QuestionFlowTypeEnum;
  isFlowLayout?: boolean;
  prefix: string;
  finishedRoute: string;
  finishedRouteParams?: unknown;
  actionQuery: unknown;
  actionQueryParams?: unknown;
  questionFlowResponseKey: string;
  defaultBackLocation?: string;
  steps?: ProgressBarProps[];
  questionSetKey: string;
  profileFieldName?: string;
  nextButtonText?: string;
  onQuestionFlowFinish?: () => void;
  onQuestionFlowKnockout?: (profile) => void;
  onQuestionFlowEmpty?: (finishedRoute, finishedRouteParams) => void;
  onCurrentAndMaxSetChange: (currentSet, maxSets) => void;
};

export const defaultQuestionFlowKnockoutAction = (profile) => {
  if (profile?.state === QuoteState.KNOCKOUT) {
    window.location = `/quote/referral/${profile?.id}`;
  }
};

export const defaultQuestionFlowEmptyAction = (
  finishedRoute: string,
  finishedRouteParams: unknown
) => {
  navigate(finishedRoute, {
    state: {
      isPreviousFlowEmpty: true,
      finishedRouteParams,
    },
  });
};

export const QuestionFlowComponent: FC<TQuestionFlowProps> = ({
  isFlowLayout = true,
  profileFieldName,
  finishedRoute,
  finishedRouteParams,
  nextButtonText = "Next",
  onQuestionFlowKnockout = defaultQuestionFlowKnockoutAction,
  onQuestionFlowEmpty = defaultQuestionFlowEmptyAction,
  ...props
}: TQuestionFlowProps) => {
  const navigate = useNavigate();

  const {
    enquiry,
    questionSet = {},
    answerEnquiry,
    currentSet,
    maxSets,
    profileData,
    loading,
    history,
  } = useStartQuestionFlow({
    questionSetKey: props.questionSetKey,
    mutationQuery: props.actionQuery,
    mutationQueryParams: props.actionQueryParams,
    globalDataKey: QuestionFlowTypeKey[props.actionFlowType],
    questionFlowResponseKey: props.questionFlowResponseKey,
    profileFieldName,
    onQuestionFlowKnockout,
  });
  const trackViewed = useTrackQuestionSetViewed({ flowType: props.actionFlowType });
  const trackCompleted = useTrackQuestionSetCompleted({
    flowType: props.actionFlowType,
  });
  const { title, description, help, rhetoricals } = questionSet;
  const initialValues = getQuestionSetAnswers(questionSet, profileData);
  const handleSubmit = async (answers) => {
    try {
      await answerEnquiry(
        enquiry?.questionFlow?.id,
        answers,
        (_currentSet: number, _maxSets: number) => {
          trackCompleted({ questionSet, answers, initialValues });
          if (_currentSet === _maxSets) {
            maybe(props?.onQuestionFlowFinish)();
            // Add finished route to history for back navigation to work
            const _history = [...history, { id: finishedRoute, key: finishedRoute }];
            navigate(finishedRoute, {
              state: {
                history: _history,
                finishedRouteParams,
              },
            });
          }
        }
      );
    } catch (e) {
      const validationErrors = e?.response?.errors?.find(
        ({ code }) => code === "question-validation"
      )?.details;
      if (validationErrors) {
        e.errors = validationErrors;
      }
      throw e;
    }
  };

  useEffect(() => {
    if (!loading && questionSet?.id) {
      trackViewed({ questionSet, initialValues });
    } else if (!loading && enquiry?.emptyQuestionFlow === true) {
      maybe(onQuestionFlowEmpty)(finishedRoute, finishedRouteParams);
    }
  }, [loading, questionSet?.id, enquiry?.emptyQuestionFlow]);

  useEffect(() => {
    if (questionSet?.key) {
      navigate(`${props.prefix}/${questionSet?.key}`);
    }
  }, [questionSet?.key]);

  useEffect(() => {
    props.onCurrentAndMaxSetChange(currentSet, maxSets);
  }, [currentSet, maxSets]);

  const flowComponent = (
    <Placeholder loading={loading}>
      <ChatFlow
        title={title}
        description={description}
        rhetoricals={rhetoricals}
        help={help}
        loading={loading}
      >
        <SidePadder>
          <QuestionSet
            questionSet={questionSet}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            key={questionSet?.id}
            nextButtonText={nextButtonText}
          />
        </SidePadder>
      </ChatFlow>
    </Placeholder>
  );

  return isFlowLayout ? (
    <SidePadder padding="flow">
      <FlowLayout
        prefix={props.prefix}
        defaultBackLocation={props.defaultBackLocation}
        steps={props.steps}
        history={history}
      >
        {flowComponent}
      </FlowLayout>
    </SidePadder>
  ) : (
    <>{flowComponent}</>
  );
};
